import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  FileText, Download, CheckCircle2, AlertTriangle, Shield, 
  Clock, Globe, Server, Lock, Users, Loader2, Eye, PenLine, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

export const PortalAVV = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [avvStatus, setAvvStatus] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fullName, setFullName] = useState('');
  const [showContract, setShowContract] = useState(false);
  
  // Address fields
  const [company, setCompany] = useState('');
  const [street, setStreet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('Deutschland');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchAVVStatus();
  }, []);

  // Pre-fill address fields from customer data
  useEffect(() => {
    if (avvStatus?.customer) {
      const c = avvStatus.customer;
      if (c.company) setCompany(c.company);
      if (c.street) setStreet(c.street);
      if (c.zip_code) setZipCode(c.zip_code);
      if (c.city) setCity(c.city);
      if (c.country) setCountry(c.country);
      if (c.first_name && c.last_name) {
        setFullName(`${c.first_name} ${c.last_name}`.trim());
      }
    }
  }, [avvStatus]);

  const fetchAVVStatus = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/portal/avv/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAvvStatus(data);
      }
    } catch (error) {
      console.error('Error fetching AVV status:', error);
      toast.error('Fehler beim Laden des AVV-Status');
    } finally {
      setLoading(false);
    }
  };

  const needsAddress = !avvStatus?.has_address;

  const handleSign = async () => {
    if (!acceptedTerms) {
      toast.error('Bitte akzeptiere den AVV');
      return;
    }
    
    if (!fullName || fullName.trim().length < 3) {
      toast.error('Bitte gib deinen vollständigen Namen ein');
      return;
    }

    // Validate address if needed
    if (needsAddress) {
      if (!street.trim()) {
        toast.error('Bitte gib deine Straße und Hausnummer ein');
        return;
      }
      if (!zipCode.trim()) {
        toast.error('Bitte gib deine Postleitzahl ein');
        return;
      }
      if (!city.trim()) {
        toast.error('Bitte gib deinen Ort ein');
        return;
      }
    }

    setSigning(true);
    try {
      const response = await fetch(`${backendUrl}/api/portal/avv/sign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accepted_terms: acceptedTerms,
          full_name: fullName.trim(),
          company: company.trim() || null,
          street: street.trim() || null,
          zip_code: zipCode.trim() || null,
          city: city.trim() || null,
          country: country.trim() || 'Deutschland'
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('AVV erfolgreich unterzeichnet!', { icon: '✅' });
        fetchAVVStatus();
      } else {
        toast.error(data.detail || 'Fehler beim Unterzeichnen');
      }
    } catch (error) {
      console.error('Error signing AVV:', error);
      toast.error('Fehler beim Unterzeichnen des AVV');
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/portal/avv/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `AVV_Wolkenhelden_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('PDF heruntergeladen');
      } else {
        toast.error('Fehler beim Herunterladen');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Fehler beim Herunterladen des PDF');
    }
  };

  const handlePreviewPDF = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/portal/avv/preview`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        toast.error('Fehler beim Laden der Vorschau');
      }
    } catch (error) {
      console.error('Error previewing PDF:', error);
      toast.error('Fehler beim Laden der Vorschau');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Already signed view
  if (avvStatus?.has_signed && avvStatus?.signature) {
    const sig = avvStatus.signature;
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Auftragsverarbeitung (AVV)</h1>
          <p className="text-muted-foreground">Vertrag zur Auftragsverarbeitung gemäß Art. 28 DSGVO</p>
        </div>

        <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-green-700 dark:text-green-400">AVV unterzeichnet</CardTitle>
                <CardDescription>
                  Dein Vertrag zur Auftragsverarbeitung ist aktiv
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Unterzeichnet am</Label>
                <p className="font-medium">
                  {new Date(sig.signed_at).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })} Uhr
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">AVV-Version</Label>
                <p className="font-medium">{sig.avv_version}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Auftraggeber</Label>
                <p className="font-medium">{sig.customer_company ? `${sig.customer_company} / ` : ''}{sig.customer_name}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">IP-Adresse bei Signatur</Label>
                <p className="font-medium font-mono text-sm">{sig.ip_address}</p>
              </div>
            </div>

            <Separator />

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Signatur-Hash (SHA-256)</Label>
              <p className="font-mono text-xs text-muted-foreground break-all bg-muted/50 p-2 rounded">
                {sig.signature_hash}
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleDownloadPDF} className="flex-1">
                <Download className="mr-2 h-4 w-4" />
                PDF herunterladen
              </Button>
            </div>
          </CardContent>
        </Card>

        {avvStatus.needs_update && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-amber-700 dark:text-amber-400">Neue Version verfügbar</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Es gibt eine aktualisierte Version des AVV. Bitte unterzeichnen du die neue Version.
              </p>
              <Button variant="outline">
                Neue Version unterzeichnen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Not signed view - show contract and signing form
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Auftragsverarbeitung (AVV)</h1>
        <p className="text-muted-foreground">Vertrag zur Auftragsverarbeitung gemäß Art. 28 DSGVO</p>
      </div>

      <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <CardTitle className="text-amber-700 dark:text-amber-400">AVV noch nicht unterzeichnet</CardTitle>
          </div>
          <CardDescription>
            Als Kunde, der personenbezogene Daten in der Nextcloud speichert, benötigst du einen 
            Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Contract Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Vertragsinhalt (Zusammenfassung)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Shield className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Datenschutz nach DSGVO</p>
                <p className="text-xs text-muted-foreground">Verarbeitung nur nach Weisung des Auftraggebers</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Globe className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Datenverarbeitung in EU/EWR</p>
                <p className="text-xs text-muted-foreground">Server ausschließlich in Deutschland</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Server className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Technische Maßnahmen (TOMs)</p>
                <p className="text-xs text-muted-foreground">ISO 27001 zertifiziertes Rechenzentrum</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Lock className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Verschlüsselung</p>
                <p className="text-xs text-muted-foreground">SSL/TLS mit Perfect Forward Secrecy</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Clock className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Tägliche Backups</p>
                <p className="text-xs text-muted-foreground">14 Tage Vorhaltezeit, georedundant</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Users className="h-5 w-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium text-sm">Unterauftragsverarbeiter</p>
                <p className="text-xs text-muted-foreground">Digital Realty (RZ), InterNetX (Domains)</p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handlePreviewPDF}>
              <Eye className="mr-2 h-4 w-4" />
              Vollständigen AVV ansehen (PDF)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contract Details (Collapsible) */}
      <Card>
        <CardHeader 
          className="cursor-pointer" 
          onClick={() => setShowContract(!showContract)}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Vollständiger Vertragstext
            </span>
            <Badge variant="outline">{showContract ? 'Ausblenden' : 'Anzeigen'}</Badge>
          </CardTitle>
        </CardHeader>
        {showContract && (
          <CardContent>
            <ScrollArea className="h-[400px] rounded-md border p-4">
              <div className="space-y-4 text-sm">
                <h3 className="font-bold text-lg">Vertrag zur Auftragsverarbeitung (AVV)</h3>
                <p className="text-muted-foreground">gemäß Art. 28 DSGVO</p>
                
                <Separator />
                
                <div>
                  <h4 className="font-semibold">1. Gegenstand und Dauer</h4>
                  <p className="text-muted-foreground mt-1">
                    Der Auftragnehmer erbringt für den Auftraggeber Dienstleistungen im Bereich 
                    Managed Nextcloud Hosting. Im Rahmen der Leistungserbringung hat der Auftragnehmer 
                    Zugriff auf personenbezogene Daten. Die Laufzeit richtet sich nach dem Hauptvertrag.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">2. Art und Zweck der Verarbeitung</h4>
                  <p className="text-muted-foreground mt-1">
                    Die Verarbeitung umfasst das Speichern, Auslesen, Ändern, Übermitteln (Backup), 
                    Sperren und Löschen von Daten auf der Infrastruktur des Auftragnehmers.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">3. Art der Daten</h4>
                  <p className="text-muted-foreground mt-1">
                    Bestandsdaten, Kontaktdaten, Inhaltsdaten (Dateien, Dokumente), 
                    Termindaten (Kalender), Verbindungsdaten (IP-Adressen, Logfiles).
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">4. Weisungsbefugnis</h4>
                  <p className="text-muted-foreground mt-1">
                    Der Auftraggeber ist für die Zulässigkeit der Verarbeitung verantwortlich. 
                    Der Auftragnehmer verarbeitet Daten nur nach dokumentierter Weisung.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">5. Pflichten des Auftragnehmers</h4>
                  <p className="text-muted-foreground mt-1">
                    Verarbeitung nur in EU/EWR, Gewährleistung der TOMs, Einsatz von 
                    verpflichtetem Personal, Unterstützung bei Betroffenenrechten.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">6. Kontrollrechte (Audit)</h4>
                  <p className="text-muted-foreground mt-1">
                    Der Auftraggeber hat das Recht zur Überprüfung. Zutrittsrecht wird durch 
                    Zertifikate (ISO 27001) ersetzt.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">7. Unterauftragsverarbeiter</h4>
                  <p className="text-muted-foreground mt-1">
                    Genehmigte Unterauftragsverarbeiter: Digital Realty GmbH (Rechenzentrum), 
                    InterNetX GmbH (Domain-Registrierung).
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">8. Meldepflicht</h4>
                  <p className="text-muted-foreground mt-1">
                    Der Auftragnehmer informiert unverzüglich über Datenpannen.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold">9. Beendigung und Löschung</h4>
                  <p className="text-muted-foreground mt-1">
                    Nach Vertragsende werden alle Daten datenschutzgerecht gelöscht oder 
                    zurückgegeben.
                  </p>
                </div>

                <Separator />

                <p className="text-xs text-muted-foreground italic">
                  Der vollständige Vertragstext inklusive Anlagen (TOMs, Unterauftragsverarbeiter) 
                  ist in der PDF-Version verfügbar.
                </p>
              </div>
            </ScrollArea>
          </CardContent>
        )}
      </Card>

      {/* Signing Form */}
      <Card className="border-primary/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5" />
            AVV digital unterzeichnen
          </CardTitle>
          <CardDescription>
            Mit der digitalen Unterzeichnung bestätigst du, dass du den AVV gelesen und akzeptiert hast.
            Deine IP-Adresse und der Zeitstempel werden rechtssicher dokumentiert.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-start space-x-3">
            <Checkbox 
              id="accept-avv" 
              checked={acceptedTerms}
              onCheckedChange={setAcceptedTerms}
              data-testid="checkbox-accept-avv"
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="accept-avv"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Ich habe den Vertrag zur Auftragsverarbeitung (AVV) gelesen und akzeptiere diesen.
              </label>
              <p className="text-xs text-muted-foreground">
                Der AVV wird Bestandteil des Hosting-Vertrages gemäß Art. 28 DSGVO.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="full-name">Vollständiger Name (zur Bestätigung)</Label>
            <Input
              id="full-name"
              placeholder="Max Mustermann"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              data-testid="input-full-name"
            />
            <p className="text-xs text-muted-foreground">
              Bitte gib deinen vollständigen Namen ein, wie er im Vertrag erscheinen soll.
            </p>
          </div>

          {/* Address Section - only shown if customer has no address */}
          {needsAddress && (
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <Label className="text-sm font-medium">Adressdaten für den AVV</Label>
              </div>
              <p className="text-xs text-muted-foreground -mt-2">
                Für den rechtsgültigen Vertrag benötigen wir deine Adresse.
              </p>
              
              <div className="space-y-2">
                <Label htmlFor="company">Firma (optional)</Label>
                <Input
                  id="company"
                  placeholder="Muster GmbH"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  data-testid="input-company"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="street">Straße und Hausnummer *</Label>
                <Input
                  id="street"
                  placeholder="Musterstraße 123"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  data-testid="input-street"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="zip-code">PLZ *</Label>
                  <Input
                    id="zip-code"
                    placeholder="12345"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    data-testid="input-zip-code"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="city">Ort *</Label>
                  <Input
                    id="city"
                    placeholder="Musterstadt"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    data-testid="input-city"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Land</Label>
                <Input
                  id="country"
                  placeholder="Deutschland"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  data-testid="input-country"
                />
              </div>
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-4 text-sm">
            <p className="font-medium mb-2">Bei der Unterzeichnung werden erfasst:</p>
            <ul className="text-muted-foreground space-y-1 text-xs">
              <li>• Deine IP-Adresse</li>
              <li>• Datum und Uhrzeit (UTC)</li>
              <li>• Browser-Information (User-Agent)</li>
              <li>• Kryptografischer Signatur-Hash (SHA-256)</li>
            </ul>
          </div>

          <Button 
            onClick={handleSign} 
            disabled={!acceptedTerms || !fullName.trim() || signing || (needsAddress && (!street.trim() || !zipCode.trim() || !city.trim()))}
            className="w-full"
            size="lg"
            data-testid="button-sign-avv"
          >
            {signing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Wird unterzeichnet...
              </>
            ) : (
              <>
                <PenLine className="mr-2 h-4 w-4" />
                AVV jetzt rechtssicher unterzeichnen
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalAVV;
